import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Search } from 'lucide-react';
import type { api } from '@stageplottter/core';
import { getApi } from '../api/endpoints';
import { hitHref, hitSelects } from '../features/search/model/target';

/** Typing pause before searching (ms). */
const DEBOUNCE_MS = 300;

/**
 * Search across all boards of the workspace (U51, OIL-25): names, objects, channels, mixes,
 * notes and rider texts. Results replace nothing – they show above the board list.
 */
export function BoardSearch({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<api.SearchResult[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const query = q.trim();
    if (query === '') return;
    let alive = true;
    const timer = setTimeout(() => {
      getApi()
        .searchBoards(workspaceId, query)
        .then(
          (r) => {
            if (!alive) return;
            setFailed(false);
            setResults(r);
          },
          () => alive && setFailed(true),
        );
    }, DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q, workspaceId]);
  const active = q.trim() !== '';
  return (
    <section className="board-search" aria-label={t('search.title')}>
      <label className="field search-field">
        <span>
          <Search size={14} aria-hidden="true" /> {t('search.label')}
        </span>
        <input
          type="search"
          value={q}
          placeholder={t('search.placeholder')}
          onChange={(e) => {
            setQ(e.target.value);
            if (e.target.value.trim() === '') setResults(null);
          }}
        />
      </label>
      {active && failed && (
        <p className="field-error" role="alert">
          {t('search.failed')}
        </p>
      )}
      {active && results !== null && results.length === 0 && (
        <p className="muted small">{t('search.none')}</p>
      )}
      {active && results !== null && results.length > 0 && (
        <ul className="search-results" aria-label={t('search.results')}>
          {results.map((r) => (
            <li key={r.board.id}>
              <Link to={`/b/${encodeURIComponent(r.board.id)}`} className="search-board">
                {r.board.name}
              </Link>
              <ul className="search-hits">
                {r.hits
                  .filter((h) => h.kind !== 'board')
                  .map((h, i) => (
                    <li key={`${h.kind}:${h.id}:${i}`}>
                      <span className="muted small">{t(`search.kind.${h.kind}`)}:</span>{' '}
                      {hitSelects(h) ? <Link to={hitHref(r.board.id, h)}>{h.text}</Link> : h.text}
                    </li>
                  ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
